import { useCallback, useMemo, useRef } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import SideMsg from '#/modules/commonComponents/SideMsg';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import {
  buildSpecInputColumns,
  buildSpecSelectColumns,
} from '#/modules/imsMgmt/settings/mtrSpec/data/specColumns';
import { mtrSpecSideMsgBlocks } from '#/modules/imsMgmt/settings/mtrSpec/data/mtrSpecSideMsgConfig';
import { getMtrSpecApiErrorMessage } from '#/modules/imsMgmt/settings/mtrSpec/helper/apiError';
import useMtrSpecList from '#/modules/imsMgmt/settings/mtrSpec/hooks/useMtrSpecList';

const MtrSpecListPage = () => {
  const selectListRef = useRef(null);
  const inputListRef = useRef(null);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.MTR_CREATE);
  const canUpdate = useCan(PERM.MTR_UPDATE);

  const {
    rows: selectRows,
    pagination: selectPagination,
    isLoading: isSelectLoading,
    isError: isSelectError,
    error: selectError,
    refetch: refetchSelect,
    listQuery: selectListQuery,
    setPage: setSelectPage,
    setPageSize: setSelectPageSize,
    setKeyword: setSelectKeyword,
    setFilter: setSelectFilter,
    removeSortAt: removeSelectSortAt,
    resetListQuery: resetSelectListQuery,
  } = useMtrSpecList('SELECT');

  const {
    rows: inputRows,
    pagination: inputPagination,
    isLoading: isInputLoading,
    isError: isInputError,
    error: inputError,
    refetch: refetchInput,
    listQuery: inputListQuery,
    setPage: setInputPage,
    setPageSize: setInputPageSize,
    setKeyword: setInputKeyword,
    setFilter: setInputFilter,
    removeSortAt: removeInputSortAt,
    resetListQuery: resetInputListQuery,
  } = useMtrSpecList('INPUT');

  const selectColumns = useMemo(
    () => buildSpecSelectColumns({
      filters: selectListQuery.filters || {},
      onFilterChange: setSelectFilter,
      canShowEdit: () => canUpdate,
    }),
    [selectListQuery.filters, setSelectFilter, canUpdate],
  );

  const inputColumns = useMemo(
    () => buildSpecInputColumns({
      filters: inputListQuery.filters || {},
      onFilterChange: setInputFilter,
      canShowEdit: () => canUpdate,
    }),
    [inputListQuery.filters, setInputFilter, canUpdate],
  );

  const selectFilterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(selectColumns),
    [selectColumns],
  );

  const inputFilterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(inputColumns),
    [inputColumns],
  );

  const handleSelectRowClick = useCallback((row, isSelected) => {
    inputListRef.current?.clearSelection?.();

    if (isSelected) {
      setSelectedData({ ...row.original, _source: 'select' });
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  const handleInputRowClick = useCallback((row, isSelected) => {
    selectListRef.current?.clearSelection?.();

    if (isSelected) {
      setSelectedData({ ...row.original, _source: 'input' });
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  const selectCreatePath = `${ROUTES.mtrSpecCreate}?specType=SELECT`;
  const inputCreatePath = `${ROUTES.mtrSpecCreate}?specType=INPUT`;

  const selectHeaderAction = canCreate ? (
    <ListAddButton to={selectCreatePath} />
  ) : null;

  const inputHeaderAction = canCreate ? (
    <ListAddButton to={inputCreatePath} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">物料規格</span>
        </h1>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <div className="grid h-full min-h-0 grid-cols-1 gap-5 @3xl/table-panel:grid-cols-2">
              <div className="flex h-full min-h-0 flex-col gap-2">
                <p className="shrink-0 text-sm font-medium text-muted-foreground">固定項目</p>
                <ListTablePanel
                  ref={selectListRef}
                  tableKey="ims.mtr_spec.list"
                  panelClassName="min-h-0 flex-1"
                  columns={selectColumns}
                  rows={selectRows}
                  pagination={selectPagination}
                  isLoading={isSelectLoading}
                  isError={isSelectError}
                  errorMessage={
                    isSelectError ? getMtrSpecApiErrorMessage(selectError, '載入列表失敗') : undefined
                  }
                  onRetry={isSelectError ? () => { refetchSelect(); } : undefined}
                  onPageChange={setSelectPage}
                  onPageSizeChange={setSelectPageSize}
                  onRowClick={handleSelectRowClick}
                  onSearch={setSelectKeyword}
                  onReset={resetSelectListQuery}
                  initialKeyword={selectListQuery.keyword}
                  searchPlaceholder="搜尋固定項目代號或名稱"
                  headerAction={selectHeaderAction}
                  listQuery={selectListQuery}
                  filterStatusFields={selectFilterStatusFields}
                  sortFieldLabels={DEFAULT_LIST_SORT_FIELD_LABELS}
                  onFilterChange={setSelectFilter}
                  onKeywordClear={() => setSelectKeyword('')}
                  onSortRemove={removeSelectSortAt}
                />
              </div>
              <div className="flex h-full min-h-0 flex-col gap-2">
                <p className="shrink-0 text-sm font-medium text-muted-foreground">輸入項目</p>
                <ListTablePanel
                  ref={inputListRef}
                  tableKey="ims.mtr_spec_option.list"
                  panelClassName="min-h-0 flex-1"
                  columns={inputColumns}
                  rows={inputRows}
                  pagination={inputPagination}
                  isLoading={isInputLoading}
                  isError={isInputError}
                  errorMessage={
                    isInputError ? getMtrSpecApiErrorMessage(inputError, '載入列表失敗') : undefined
                  }
                  onRetry={isInputError ? () => { refetchInput(); } : undefined}
                  onPageChange={setInputPage}
                  onPageSizeChange={setInputPageSize}
                  onRowClick={handleInputRowClick}
                  onSearch={setInputKeyword}
                  onReset={resetInputListQuery}
                  initialKeyword={inputListQuery.keyword}
                  searchPlaceholder="搜尋輸入項目代號或名稱"
                  headerAction={inputHeaderAction}
                  listQuery={inputListQuery}
                  filterStatusFields={inputFilterStatusFields}
                  sortFieldLabels={DEFAULT_LIST_SORT_FIELD_LABELS}
                  onFilterChange={setInputFilter}
                  onKeywordClear={() => setInputKeyword('')}
                  onSortRemove={removeInputSortAt}
                />
              </div>
            </div>
          </div>

          <SideMsg
            title="規格"
            titleValue="name"
            displayData={displayData}
            blocks={mtrSpecSideMsgBlocks}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default MtrSpecListPage;
